import { MaterialRequisitionProjectResourceModel } from "api/models/company/material-requisition/material-requisition-project-resource-model";
import { autoinject, observable } from "aurelia-framework";
import { I18N } from "aurelia-i18n";

import { default as dateHelper } from "helpers/dateHelper";
import { default as settingHelper } from "helpers/settingHelper";

import { DispatchProjectMaterialRequisitionProxy } from "api/proxies/dispatch-project-material-requisition-proxy";
import { navigateBack } from "helpers/routerHelper";
import { MaterialRequisitionItemModel } from "api/models/company/material-requisition/material-requisition-item-model";
import { PurchaseOrderItemModel } from "api/models/company/purchase-order-item-model";
import { PurchaseOrderService } from "services/purchase-order-service";
import { CatalogItemPriceService } from "services/catalog-item-price-service";
import { CatalogItemPriceModel } from "api/models/company/catalog/catalog-item-price-model";
import { UnitPriceType } from "api/enums/unit-price-type";
import _ from "underscore";
import { NotificationHelper } from "helpers/notification-helper";
import { Router } from "aurelia-router";
import { FormBase } from "pages/form-base";

@autoinject()
export default class MaterialOrderProjectResource extends FormBase {
    public lineNo: any = "";
    public orderDate: Date | null = null;
    public origOrderDate: Date | null = null;
    public orderId: string = "";
    public extension: string = "";
    public supplierCode: string = "";
    public isResourceSelected: boolean = true;
    public itemSaved: boolean = false;
    public isSaving: boolean = false;

    public dispatchProjectCode: any;

    public resourceItems: MaterialRequisitionItemModel[] = [];

    @observable public selectedResource: any | undefined;

    public getProjectResources: any = {
        transport: (params: any, success: any, failure: any): any => {
            this.dispatchProjectMaterialRequisitionProxy
                .GetMaterialRequisitionProjectResources(this.dispatchProjectCode, settingHelper.getSelectedDispatchModel())
                .then(
                    (result: any) => {
                        return success(result);
                    },
                    (fail: any) => {
                        return failure(fail);
                    }
                );
        },
        mapResults: (item: MaterialRequisitionProjectResourceModel): any => {
            return { id: item.Number, text: `${item.Number} - ${item.Description}`, data: item };
        },
    };

    constructor(
        i18n: I18N,
        notificationHelper: NotificationHelper,
        router: Router,
        private readonly dispatchProjectMaterialRequisitionProxy: DispatchProjectMaterialRequisitionProxy,
        private readonly purchaseOrderService: PurchaseOrderService,
        private readonly catalogItemPriceService: CatalogItemPriceService
    ) {
        super(notificationHelper, i18n, router);
    }

    public checkDirty(): boolean {
        if (this.itemSaved) {
            return false;
        }

        if (!this.isResourceSelected) {
            return true;
        }

        if (this.resourceItems.length > 0) {
            return true;
        }

        return false;
    }

    public selectedResourceChanged(newValue: any): void {
        this.dispatchProjectMaterialRequisitionProxy
            .GetMaterialRequisitionProjectResourceItems(this.dispatchProjectCode, newValue.id, settingHelper.getSelectedDispatchModel())
            .then((result: MaterialRequisitionItemModel[]) => {
                this.resourceItems = result;
                this.isResourceSelected = false;
            });
    }

    public async saveResourceItems(): Promise<void> {
        const validItemsForSave = this.getValidResourceItems();

        if (!this.isResourceSelected && this.isSaveAllowed(validItemsForSave)) {
            this.isSaving = true;

            try {
                const prices = await this.fetchPricesForItems(validItemsForSave);
                const purchaseOrderItems: Array<PurchaseOrderItemModel | null> = this.mapToPurchaseOrderItems(validItemsForSave, prices);

                await this.purchaseOrderService.SavePurchaseOrderDetailForProject(this.orderId, 0, purchaseOrderItems);
                this.itemSaved = true;
                navigateBack();
            } finally {
                this.isSaving = false;
            }
        }
    }

    public resetQuantityResourceItems(): void {
       this.resourceItems.forEach((item: MaterialRequisitionItemModel) => {
           item.QuantityRequired = 0;
       });
    }

    public activate(params: any): void {
        this.dispatchProjectCode = params.dispatchProjectCode;
        this.orderId = params.orderId;
        this.extension = params.extension || "";
        this.orderDate = dateHelper.dateFromNavigationParameter(params.date);
        this.origOrderDate = dateHelper.dateFromNavigationParameter(params.date);
        this.lineNo = params.lineNo;
        this.supplierCode = params.supplierCode && params.supplierCode !== "_" ? decodeURIComponent(params.supplierCode) : "";
    }

    private isSaveAllowed(validItemsForSave: MaterialRequisitionItemModel[]): boolean {
        if (validItemsForSave.length < 1) {
            this.notificationHelper.showWarning(this.i18n.tr("WarningMustHaveOne"), "");
            return false;
        }
        return true;
    }

    private async fetchPricesForItems(items: MaterialRequisitionItemModel[]): Promise<CatalogItemPriceModel[]> {
        const prices: CatalogItemPriceModel[] = [];
        const defaultPrice: CatalogItemPriceModel = { ProductCode: "", UpType: UnitPriceType.Order, Quantity: 0, TransactionDate: new Date(), SupplierCode: null, ProjectNo: null, SupplierFactor: 0, InventoryFactor: 0, InventoryUnitPrice: 0, SupplierUnitPrice: 0, DiscountPercent: 0 };

        for (const item of items) {
            try {
                const price = await this.catalogItemPriceService.getUnitPrice(
                    item.Code || "",
                    UnitPriceType.Order,
                    item.QuantityRequired,
                    this.orderDate!,
                    1,
                    1,
                    this.supplierCode || null,
                    this.dispatchProjectCode || null
                );
                prices.push(price);
            } catch {
                prices.push(defaultPrice);
            }
        }

        return prices;
    }

    private mapToPurchaseOrderItems(resourceItems: MaterialRequisitionItemModel[], prices: CatalogItemPriceModel[]): Array<PurchaseOrderItemModel | null> {
        return resourceItems.map((item: MaterialRequisitionItemModel, index: number) => {
            const price = prices[index];
            return {
                DispatchId: 0,
                Line: 0,
                Code: item.Code,
                OutCatalogCode: null,
                Description: item.Description,
                Description2: null,
                Description3: null,
                Quantity: item.QuantityRequired,
                Extension: this.extension || null,
                OrderId: this.orderId,
                OrderDate: this.orderDate,
                UnitDescription: item.UnitDescription,
                SupplierUnitDescription: null,
                Activity: item.Activity,
                Group: item.Group,
                UnitPrice: price.InventoryUnitPrice,
                SupplierUnitPrice: price.SupplierUnitPrice,
                SupplierFactor: 0,
                SupplierQuantity: item.QuantityRequired,
                InventoryFactor: 0,
                InventoryScaleFactor: item.InventoryScaleFactor || 0,
                SupplierScaleFactor: 0,
                DiscountPercent: price.DiscountPercent,
                TotalAmountSupplier: 0,
            } as PurchaseOrderItemModel;
        });
    }

    private getValidResourceItems(): MaterialRequisitionItemModel[] {
        return _.filter(
            this.resourceItems,
            (item: MaterialRequisitionItemModel): boolean => {
                return item.QuantityRequired > 0;
            }
        );
    }
}
